import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatIconModule } from '@angular/material/icon';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ChartData, ChartOptions } from 'chart.js';
import { Subject, timeout } from 'rxjs';
import { ClientAnalyticsControllerService, ClientBrandTagResponse, ClientCoverageDashboardRequest, ClientCoverageDashboardResponse } from 'api-clipping';
import { UtilsService } from '../../../../shared/services/utils.service';
import { ChartComponent } from '../../../../shared/components/sette/chart/chart.component';
import { KpiTileComponent } from '../../../../shared/components/sette/kpi-tile/kpi-tile.component';
import { SampleSizeNoteComponent } from '../../../../shared/components/sette/sample-size-note/sample-size-note.component';
import { AnalyticsSkeletonComponent } from '../../../../shared/components/sette/analytics-skeleton/analytics-skeleton.component';
import { AnalyticsErrorStateComponent, AnalyticsErrorKind } from '../../../../shared/components/sette/analytics-error-state/analytics-error-state.component';
import { AnalyticsEmptyStateComponent } from '../../../../shared/components/sette/analytics-empty-state/analytics-empty-state.component';
import { InfoIconComponent } from '../../../../shared/components/sette/info-icon/info-icon.component';
import { FilterState } from '../filter-bar/filter-bar.component';
import { buildAnalyticsFilterRequest, isCompareRequested, resolveComparison, classifyAnalyticsError, createAnalyticsFetchStream, ANALYTICS_REQUEST_TIMEOUT_MS, noMentionsLabel, isNewMetric, newMetricLabel } from '../shared/analytics-request.util';
import { signTone, formatSigned, formatSignedAuto, formatAnalyticsNumber, formatAnalyticsInt, formatAnalyticsFixed, formatAnalyticsAxisDate, seqTextColor, categoricalColors, brandColor, MEDIA_COLORS, SENTIMENT_COLORS, AXIS, Tone } from '../../../../shared/components/sette/chart-theme';
import { translateSourceType } from '../shared/taxonomy.util';

type BrandMetric = 'Volume' | 'Reach' | 'Impact';

interface BrandRowView {
	brand: string;
	value: number;
	changeStr: string;
	sov: string;
	color: string;
	avgReach: string;
	avgReachValue: number;
	avgImpact: string;
	avgImpactValue: number;
	impactTone: Tone;
	isLowData: boolean;
	isClient: boolean;
}

interface MediaTypeRowView {
	name: string;
	value: number;
	percentage: string;
	color: string;
}

interface TopMediaRowView {
	rank: number;
	media: string;
	type: string;
	mentions: number;
	barPercent: number;
	reach: string;
	impact: string;
	impactTone: Tone;
}

interface TopAuthorRowView {
	rank: number;
	author: string;
	media: string;
	mentions: number;
	barPercent: number;
	reach: string;
	impact: string;
	impactTone: Tone;
}

interface SourceTypeRowView {
	name: string;
	value: number;
	percentage: string;
	color: string;
}

interface MatrixCellView {
	count: number;
	impact: number;
}

interface MatrixRowView {
	mediaId: string;
	media: string;
	total: number;
	cells: Record<string, MatrixCellView>;
}

interface MatrixColumnView {
	key: string;
	label: string;
	color: string;
}

interface CalendarDayView {
	day: number;
	date: string;
	val: number;
}

interface KpiView {
	value: string;
	trendUp?: boolean;
	deltaLabel: string;
}

// SETTE redesign (CLIPPING-598): source-type rows have no fixed identity, so they take the
// categorical chart palette (avoiding chart-5, which this screen's Impact data would read as
// "negative"). Media type IS a fixed identity -> MEDIA_COLORS, same as every other screen. Brand
// identity now goes through the shared brandColor() (Задача 8) instead of this screen's own palette.
// Backend media-type codes -> the same ANALYTICS_FILTER_BAR.MEDIA_TYPE_* keys the Filters drawer
// already uses for these exact labels (Web Portals/Television/Print Press/Radio/Social Media).
const MEDIA_TYPE_LABEL_KEYS: Record<string, string> = {
	WEB: 'ANALYTICS_FILTER_BAR.MEDIA_TYPE_WEB',
	TV: 'ANALYTICS_FILTER_BAR.MEDIA_TYPE_TV',
	PRINTED: 'ANALYTICS_FILTER_BAR.MEDIA_TYPE_PRINT',
	RADIO: 'ANALYTICS_FILTER_BAR.MEDIA_TYPE_RADIO',
	SOCIAL: 'ANALYTICS_FILTER_BAR.MEDIA_TYPE_SOCIAL',
};
const MEDIA_TYPE_COLORS: Record<string, string> = { WEB: MEDIA_COLORS.portal, TV: MEDIA_COLORS.tv, PRINTED: MEDIA_COLORS.print, RADIO: MEDIA_COLORS.radio, SOCIAL: '#7D8796' };
const SOURCE_TYPE_COLORS = categoricalColors(8, { avoidSentimentClash: true });

// Ported from analytics-design/src/components/CoverageDashboard.tsx, then rewired to call
// POST /v1/client/analytics/coverage-dashboard instead of using sample data. Two necessary
// structural changes from the original markup: the Media x Brand Matrix table had 6 hardcoded
// brand columns (A1/Telekom/Mtel/One/Lyca/Telekabel) - real clients can have any number of
// selected brands, so that table now iterates matrixColumns dynamically. The Coverage Calendar
// also assumed a fixed Q2-2025 month grid with prev/next-month padding cells - real filter
// periods are arbitrary date ranges, so it now renders just the selected period's days with
// leading blanks to align the first day to its weekday column, no trailing-month padding.
// Both trend-chart "Prev Period" comparison lines were dropped - the backend's per-bucket trend
// only carries the current period's numbers (same as Overview's own trend chart).
@Component({
	selector: 'app-coverage-dashboard',
	standalone: true,
	imports: [MatIconModule, TranslateModule, ChartComponent, KpiTileComponent, SampleSizeNoteComponent, AnalyticsSkeletonComponent, AnalyticsErrorStateComponent, AnalyticsEmptyStateComponent, InfoIconComponent],
	templateUrl: './coverage-dashboard.component.html',
	styleUrl: './coverage-dashboard.component.scss',
})
export class CoverageDashboardComponent implements OnChanges {
	@Input() filters!: FilterState;
	@Input() brandOptions: ClientBrandTagResponse[] = [];
	@Output() navigate = new EventEmitter<string>();

	loading = false;
	loadError = false;
	errorState: AnalyticsErrorKind = { kind: 'generic' };
	data: ClientCoverageDashboardResponse | null = null;

	brandMetric: BrandMetric = 'Volume';
	readonly brandMetricOptions: BrandMetric[] = ['Volume', 'Reach', 'Impact'];
	private readonly fetchTrigger = new Subject<ClientCoverageDashboardRequest>();

	constructor(
		private readonly api: ClientAnalyticsControllerService,
		private readonly utils: UtilsService,
		private readonly translate: TranslateService,
	) {
		// Задача 7: switchMap-based stale-response guard - see createAnalyticsFetchStream's doc.
		createAnalyticsFetchStream(this.fetchTrigger, (request) => this.api.getCoverageDashboardAnalytics(request).pipe(timeout(ANALYTICS_REQUEST_TIMEOUT_MS)))
			.pipe(takeUntilDestroyed())
			.subscribe((result) => {
				this.loading = false;
				if (result.ok) {
					this.data = result.data!;
				} else {
					this.loadError = true;
					this.errorState = classifyAnalyticsError(result.error);
				}
			});
	}

	get lang(): string {
		return this.utils.currentLanguage;
	}

	// Задача 7: locale-formatted mention count for the raw `number` fields the templates bind
	// directly (bar-chart value labels) - kept as `number` on the view models themselves since
	// they also drive width/sort math (maxBrandValue, maxSourceTypeValue, ...).
	fmtInt(n: number): string {
		return formatAnalyticsInt(n, this.lang);
	}

	mediaTypeLabel(code: string): string {
		const key = MEDIA_TYPE_LABEL_KEYS[code];
		return key ? this.translate.instant(key) : code;
	}

	get showSkeleton(): boolean {
		return this.loading && !this.data;
	}

	get isRefreshing(): boolean {
		return this.loading && !!this.data;
	}

	get isEmptyPeriod(): boolean {
		return !this.loading && !this.loadError && (this.data?.mentions?.value ?? 0) === 0;
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (changes['filters']) {
			this.fetch();
		}
	}

	fetch(): void {
		const fields = buildAnalyticsFilterRequest(this.filters);
		if (!fields) {
			return;
		}
		this.loading = true;
		this.loadError = false;
		this.fetchTrigger.next(fields as ClientCoverageDashboardRequest);
	}

	setBrandMetric(metric: BrandMetric): void {
		this.brandMetric = metric;
	}

	brandMetricLabel(metric: BrandMetric): string {
		const key = metric === 'Reach' ? 'METRIC_VISIBILITY' : metric === 'Impact' ? 'METRIC_IMPACT' : 'METRIC_VOLUME';
		return this.translate.instant(`ANALYTICS_COVERAGE_DASHBOARD.${key}`);
	}

	onNavigate(targetId: string): void {
		this.navigate.emit(targetId);
	}

	private get focusBrandName(): string {
		const selected = this.filters?.brands ?? [];
		const company = this.brandOptions.find((b) => b.brandKind === 'COMPANY');
		const focusId = selected.length === 1 ? selected[0] : company?.tagId && selected.includes(company.tagId) ? company.tagId : selected[0];
		return this.brandOptions.find((b) => b.tagId === focusId)?.name || '';
	}

	private get poolMentions(): number {
		return (this.data?.brands ?? []).reduce((sum, b) => sum + (b.mentions ?? 0), 0);
	}

	private kpi(
		value: number | undefined,
		previousValue: number | undefined | null,
		hasEnoughCompareData: boolean | undefined,
		format: (n: number) => string,
		deltaSuffix: 'pts' | '%' | 'pp'
	): KpiView {
		// Analytics accuracy review follow-up (2026-11-01): never hide a number for being small -
		// only a genuinely empty period (0 mentions, value null) has nothing to show at all.
		if (value == null) {
			return { value: '—', deltaLabel: noMentionsLabel(this.translate) };
		}
		const comparison = resolveComparison(isCompareRequested(this.filters), previousValue, hasEnoughCompareData);
		if (comparison.insufficient) {
			// No usable previous value at all (the compare period had 0 mentions) - "Ново" if this
			// period has a real value to report, otherwise nothing to say.
			return { value: format(value), deltaLabel: isNewMetric(value, 0) ? newMetricLabel(this.translate) : '' };
		}
		if (!comparison.show) {
			return { value: format(value), deltaLabel: '' };
		}
		const prev = comparison.previous as number;
		const delta = value - prev;
		const deltaLabel =
			deltaSuffix === '%' && isNewMetric(value, prev)
				? `${newMetricLabel(this.translate)} (${format(prev)} → ${format(value)})`
				: deltaSuffix === '%'
					? `${delta >= 0 ? '+' : '-'}${formatAnalyticsInt(Math.abs(prev !== 0 ? Math.round((delta / prev) * 100) : 0), this.lang)}% (${format(prev)} → ${format(value)})`
					: `${delta >= 0 ? '+' : '-'}${formatAnalyticsNumber(Math.abs(delta), this.lang, 1)} ${deltaSuffix} (${format(prev)} → ${format(value)})`;
		return { value: format(value), trendUp: value >= prev, deltaLabel };
	}

	get mentionsKpi(): KpiView {
		return this.kpi(this.data?.mentions?.value, this.data?.mentions?.previousValue, this.data?.mentions?.hasEnoughCompareData, (n) => formatAnalyticsInt(Math.round(n), this.lang), '%');
	}

	get avgReachKpi(): KpiView {
		return this.kpi(this.data?.avgReach?.value, this.data?.avgReach?.previousValue, this.data?.avgReach?.hasEnoughCompareData, (n) => `${formatAnalyticsNumber(n, this.lang, 1)}/100`, 'pts');
	}

	get avgImpactKpi(): KpiView {
		return this.kpi(this.data?.avgImpact?.value, this.data?.avgImpact?.previousValue, this.data?.avgImpact?.hasEnoughCompareData, (n) => (n >= 0 ? '+' : '-') + formatAnalyticsNumber(n, this.lang, 1), 'pts');
	}

	// SETTE redesign (CLIPPING-598): KpiTile's value is always text-ink (Правило В) - this only
	// drives the sign on the delta pill and sparkline. true/undefined -> blue, false -> negative,
	// matching "KPI sparkline е секогаш сина, освен кога трендот... е ЛОШ".
	sparklineColorFor(trendUp?: boolean): string {
		return trendUp === false ? '#B4321F' : '#244896';
	}

	toneClasses(tone: Tone): string {
		if (tone === 'positive') return 'bg-positive-tint text-positive';
		if (tone === 'negative') return 'bg-negative-tint text-negative';
		return 'bg-neutral-tint text-ink-subtle';
	}

	get shareOfVoiceKpi(): KpiView {
		return this.kpi(this.data?.shareOfVoice?.value, this.data?.shareOfVoice?.previousValue, this.data?.shareOfVoice?.hasEnoughCompareData, (n) => `${formatAnalyticsNumber(n, this.lang, 1)}%`, 'pp');
	}

	// "Врз основа на N спомнувања" captions under the 3 trend charts - same sampleSize already
	// backing each chart's own KPI card above, so the chart and its card never disagree on N.
	get mentionsSampleSize(): number | undefined {
		return this.data?.mentions?.sampleSize;
	}

	get avgReachSampleSize(): number | undefined {
		return this.data?.avgReach?.sampleSize;
	}

	get avgImpactSampleSize(): number | undefined {
		return this.data?.avgImpact?.sampleSize;
	}

	get marketTotalNote(): string {
		return this.translate.instant('ANALYTICS_COVERAGE_DASHBOARD.MARKET_TOTAL', { total: formatAnalyticsInt(this.poolMentions, this.lang) });
	}

	get ourBrandNote(): string {
		return this.translate.instant('ANALYTICS_FILTER_BAR.OUR_BRAND_WITH_NAME', { name: this.focusBrandName });
	}

	get sparkMentions(): number[] {
		return (this.data?.trend ?? []).slice(-7).map((t) => t.mentions ?? 0);
	}

	get sparkReach(): number[] {
		return (this.data?.trend ?? []).slice(-7).map((t) => t.avgReach ?? 0);
	}

	get sparkImpactSigned(): number[] {
		return (this.data?.trend ?? []).slice(-7).map((t) => t.avgImpact ?? 0);
	}

	// --- Trend charts (mentions / reach / impact), one series each from the shared trend array ---

	private trendLabels(): string[] {
		return (this.data?.trend ?? []).map((t) => (t.bucketStart ? formatAnalyticsAxisDate(new Date(t.bucketStart), this.lang) : ''));
	}

	// SETTE redesign (CLIPPING-598): Правило А - Mentions (volume) and Avg Visibility (0-100)
	// carry no sign, so neither is green; Impact IS signed, so its line is blue (chart-1) with
	// the sign carried by each point's color (signTone), not the line.
	get coverageTrendChartData(): ChartData {
		return {
			labels: this.trendLabels(),
			datasets: [
				{
					label: this.translate.instant('ANALYTICS_COVERAGE_DASHBOARD.TOTAL_MENTIONS_LEGEND'),
					data: (this.data?.trend ?? []).map((t) => t.mentions ?? 0),
					borderColor: '#244896',
					backgroundColor: '#244896',
					borderWidth: 2.2,
					pointRadius: 2.5,
					pointBackgroundColor: '#244896',
					pointHoverRadius: 5,
					tension: 0.4,
				},
			],
		};
	}

	get coverageTrendChartOptions(): ChartOptions {
		return this.lineOptions(0, undefined, 3);
	}

	get reachTrendChartData(): ChartData {
		return {
			labels: this.trendLabels(),
			datasets: [
				{
					label: this.translate.instant('ANALYTICS_COVERAGE_DASHBOARD.AVG_VISIBILITY_HEADER'),
					data: (this.data?.trend ?? []).map((t) => t.avgReach ?? null),
					borderColor: '#7B5CC4',
					backgroundColor: '#7B5CC4',
					borderWidth: 2.2,
					pointRadius: 2.5,
					pointBackgroundColor: '#7B5CC4',
					pointHoverRadius: 5,
					tension: 0.4,
					spanGaps: true,
				},
			],
		};
	}

	get reachTrendChartOptions(): ChartOptions {
		return this.lineOptions(0, 100, 25);
	}

	get impactTrendChartData(): ChartData {
		return {
			labels: this.trendLabels(),
			datasets: [
				{
					label: this.translate.instant('ANALYTICS_COVERAGE_DASHBOARD.ZERO_BASELINE_DATASET_LABEL'),
					data: (this.data?.trend ?? []).map(() => 0),
					borderColor: AXIS.baseline,
					borderDash: [3, 3],
					borderWidth: 1.2,
					pointRadius: 0,
					tension: 0,
				},
				{
					label: this.translate.instant('ANALYTICS_COVERAGE_DASHBOARD.AVG_IMPACT_HEADER'),
					data: (this.data?.trend ?? []).map((t) => t.avgImpact ?? null),
					borderColor: '#244896',
					borderWidth: 2.4,
					pointRadius: 3,
					pointBorderColor: '#ffffff',
					pointBorderWidth: 1,
					pointBackgroundColor: (this.data?.trend ?? []).map((t) => SENTIMENT_COLORS[signTone(t.avgImpact ?? 0)]),
					pointHoverRadius: 5,
					tension: 0.4,
					spanGaps: true,
				},
			],
		};
	}

	get impactTrendChartOptions(): ChartOptions {
		return {
			responsive: true,
			maintainAspectRatio: false,
			interaction: { mode: 'index', intersect: false },
			plugins: {
				legend: { display: false },
				tooltip: {
					backgroundColor: AXIS.tooltipBg,
					bodyColor: '#ffffff',
					titleColor: '#ffffff',
					borderWidth: 0,
					cornerRadius: 10,
					bodyFont: { size: 11 },
					titleFont: { size: 11 },
					filter: (item: any) => item.datasetIndex === 1,
					callbacks: { label: (item: any) => `${formatSignedAuto(Number(item.raw), this.lang)} pts (${this.translate.instant('ANALYTICS_COVERAGE_DASHBOARD.AVG_IMPACT_HEADER')})` },
				},
			},
			scales: {
				x: { grid: { display: false }, ticks: { color: AXIS.text, font: { size: 9 } } },
				y: { min: -100, max: 100, ticks: { stepSize: 50, color: AXIS.text, font: { size: 9 }, callback: (v) => formatSignedAuto(Number(v), this.lang) }, grid: { color: AXIS.grid } },
			},
		};
	}

	private lineOptions(min: number, max: number | undefined, stepSize: number): ChartOptions {
		return {
			responsive: true,
			maintainAspectRatio: false,
			interaction: { mode: 'index', intersect: false },
			plugins: {
				legend: { display: false },
				tooltip: { backgroundColor: AXIS.tooltipBg, bodyColor: '#ffffff', titleColor: '#ffffff', borderWidth: 0, cornerRadius: 10, bodyFont: { size: 11 }, titleFont: { size: 11 } },
			},
			scales: {
				x: { grid: { display: false }, ticks: { color: AXIS.text, font: { size: 9 } } },
				y: { min, max, ticks: { stepSize, color: AXIS.text, font: { size: 9 } }, grid: { color: AXIS.grid } },
			},
		};
	}

	// --- Brand comparison table ---

	get sortedBrandData(): BrandRowView[] {
		const rows: BrandRowView[] = (this.data?.brands ?? []).map((b) => ({
			brand: b.name || '',
			value: b.mentions ?? 0,
			changeStr: this.changeStr(b.mentions, b.previousMentions),
			sov: `${formatAnalyticsNumber(b.sharePercent ?? 0, this.lang, 1)}%`,
			color: brandColor(b.tagId, this.brandOptions),
			avgReach: b.avgReach != null ? `${formatAnalyticsNumber(b.avgReach, this.lang, 1)}/100` : '—',
			avgReachValue: b.avgReach ?? 0,
			avgImpact: b.avgImpact != null ? formatSigned(b.avgImpact, 1, this.lang) : '—',
			avgImpactValue: b.avgImpact ?? 0,
			impactTone: signTone(b.avgImpact ?? 0),
			isLowData: !b.hasEnoughData,
			isClient: b.brandKind === 'COMPANY',
		}));
		if (this.brandMetric === 'Reach') return [...rows].sort((a, b) => b.avgReachValue - a.avgReachValue);
		if (this.brandMetric === 'Impact') return [...rows].sort((a, b) => b.avgImpactValue - a.avgImpactValue);
		return [...rows].sort((a, b) => b.value - a.value);
	}

	private changeStr(current?: number, previous?: number | null): string {
		if (current == null) return '';
		if (previous == null) return `${formatAnalyticsInt(current, this.lang)} ${this.translate.instant('ANALYTICS_GLOSSARY.MENTIONS')}`;
		const diff = current - previous;
		if (isNewMetric(current, previous)) {
			return `${newMetricLabel(this.translate)} (${formatAnalyticsInt(previous, this.lang)} → ${formatAnalyticsInt(current, this.lang)})`;
		}
		const pct = previous > 0 ? Math.round((diff / previous) * 100) : 0;
		return `${diff >= 0 ? '+' : '-'}${formatAnalyticsInt(Math.abs(pct), this.lang)}% (${formatAnalyticsInt(previous, this.lang)} → ${formatAnalyticsInt(current, this.lang)})`;
	}

	get maxBrandValue(): number {
		return Math.max(1, ...this.sortedBrandData.map((r) => r.value));
	}

	get brandSummaryLine(): string {
		const top = this.sortedBrandData[0];
		if (!top) return '';
		return this.translate.instant('ANALYTICS_COVERAGE_DASHBOARD.BRAND_SUMMARY_LINE', {
			brand: top.brand,
			value: formatAnalyticsInt(top.value, this.lang),
			sov: top.sov,
			avgReach: top.avgReach,
			avgImpact: top.avgImpact,
		});
	}

	// --- Media type doughnut ---

	get mediaTypeData(): MediaTypeRowView[] {
		const rows = this.data?.mediaTypes ?? [];
		const total = rows.reduce((s, r) => s + (r.mentions ?? 0), 0);
		return rows.map((r) => ({
			name: this.mediaTypeLabel(r.type || ''),
			value: r.mentions ?? 0,
			percentage: total > 0 ? `${formatAnalyticsNumber(((r.mentions ?? 0) / total) * 100, this.lang, 1)}%` : '0%',
			color: MEDIA_TYPE_COLORS[r.type || ''] || '#7D8796',
		}));
	}

	get mediaTypeTotal(): number {
		return this.mediaTypeData.reduce((s, r) => s + r.value, 0);
	}

	get mediaTypeChartData(): ChartData {
		return {
			labels: this.mediaTypeData.map((m) => m.name),
			datasets: [{ data: this.mediaTypeData.map((m) => m.value), backgroundColor: this.mediaTypeData.map((m) => m.color), borderWidth: 0, spacing: 2 }],
		};
	}

	readonly mediaTypeChartOptions = {
		responsive: true,
		maintainAspectRatio: false,
		cutout: '67%',
		plugins: { legend: { display: false }, tooltip: { enabled: false } },
	};

	get mediaTypeSummaryLine(): string {
		const top = this.mediaTypeData[0];
		if (!top) return '';
		return this.translate.instant('ANALYTICS_COVERAGE_DASHBOARD.MEDIA_TYPE_SUMMARY_LINE', {
			name: top.name,
			percentage: top.percentage,
			value: formatAnalyticsInt(top.value, this.lang),
			total: formatAnalyticsInt(this.mediaTypeTotal, this.lang),
		});
	}

	// --- Top media table ---

	get topMediaData(): TopMediaRowView[] {
		const rows = this.data?.topMedia ?? [];
		const max = Math.max(1, ...rows.map((r) => r.mentions ?? 0));
		// Analytics accuracy review follow-up (2026-11-01): the old "Премалку" (TOO_FEW_SHORT) text
		// here was a workaround for the since-removed 10-mention floor (Пакет А #12) - avgReach/
		// avgImpact are now only null at genuinely 0 mentions, which a row already in topMedia
		// never has, so a plain '—' is correct again, same as every other empty value on this screen.
		return rows.map((r, i) => ({
			rank: i + 1,
			media: r.name || '',
			type: this.mediaTypeLabel(r.type || ''),
			mentions: r.mentions ?? 0,
			barPercent: ((r.mentions ?? 0) / max) * 100,
			reach: r.avgReach != null ? formatAnalyticsNumber(r.avgReach, this.lang, 1) : '—',
			impact: r.avgImpact != null ? formatSigned(r.avgImpact, 1, this.lang) : '—',
			impactTone: signTone(r.avgImpact ?? 0),
		}));
	}

	// --- Top authors table ---

	get topAuthorsData(): TopAuthorRowView[] {
		const rows = this.data?.topAuthors ?? [];
		const max = Math.max(1, ...rows.map((r) => r.mentions ?? 0));
		return rows.map((r, i) => ({
			rank: i + 1,
			author: r.name || '',
			media: r.mediumName || '',
			mentions: r.mentions ?? 0,
			barPercent: ((r.mentions ?? 0) / max) * 100,
			reach: r.avgReach != null ? formatAnalyticsNumber(r.avgReach, this.lang, 1) : '—',
			impact: r.avgImpact != null ? formatSigned(r.avgImpact, 1, this.lang) : '—',
			impactTone: signTone(r.avgImpact ?? 0),
		}));
	}

	// --- Source type ---

	get sourceTypeData(): SourceTypeRowView[] {
		const rows = this.data?.sourceTypes ?? [];
		return rows.map((r, i) => ({
			name: translateSourceType(r.sourceType, this.translate),
			value: r.mentions ?? 0,
			percentage: r.sharePercent != null ? `${formatAnalyticsNumber(r.sharePercent, this.lang, 1)}%` : '0%',
			color: SOURCE_TYPE_COLORS[i % SOURCE_TYPE_COLORS.length],
		}));
	}

	get maxSourceTypeValue(): number {
		return Math.max(1, ...this.sourceTypeData.map((r) => r.value));
	}

	get sourceTypeSummaryLine(): string {
		const top = this.sourceTypeData[0];
		if (!top) return this.translate.instant('ANALYTICS_COVERAGE_DASHBOARD.NO_SOURCE_TYPE_DATA');
		return this.translate.instant('ANALYTICS_COVERAGE_DASHBOARD.SOURCE_TYPE_SUMMARY_LINE', { name: top.name, percentage: top.percentage });
	}

	// --- Media x Brand matrix (dynamic columns - real brand count/names, not a fixed 6) ---

	get matrixColumns(): MatrixColumnView[] {
		return (this.data?.mediaBrandMatrix?.columns ?? []).map((c) => ({ key: c.key || '', label: c.label || c.key || '', color: brandColor(c.key, this.brandOptions) }));
	}

	get mediaBrandMatrix(): MatrixRowView[] {
		return (this.data?.mediaBrandMatrix?.rows ?? []).slice(0, 12).map((r) => ({
			mediaId: r.rowKey || '',
			media: r.rowLabel || r.rowKey || '',
			total: r.totalMentions ?? 0,
			cells: Object.fromEntries(Object.entries(r.cellsByColumnKey ?? {}).map(([k, v]) => [k, { count: v.mentions ?? 0, impact: v.avgImpact ?? 0 }])),
		}));
	}

	cellFor(row: MatrixRowView, key: string): MatrixCellView {
		return row.cells[key] ?? { count: 0, impact: 0 };
	}

	// SETTE redesign (CLIPPING-598), Правило Г: Media x Brand is a volume heatmap, not a
	// sign/sentiment one - intensity (mention count, relative to the matrix's own max) through
	// SEQ_COLORS + seqTextColor, same convention as the calendar below. Avg impact stays
	// available in the cell's title tooltip (matrixCellTitle), it's just not what colors the
	// cell anymore.
	get maxMatrixCount(): number {
		return Math.max(1, ...this.mediaBrandMatrix.flatMap((r) => Object.values(r.cells).map((c) => c.count)));
	}

	getImpactHeatmapBg(count: number): string {
		if (count === 0) return 'bg-page text-ink-subtle font-normal';
		const n = Math.min(5, Math.max(2, Math.ceil((count / this.maxMatrixCount) * 5)));
		return `bg-seq-${n} ${seqTextColor(n - 1)} font-medium`;
	}

	matrixCellTitle(media: string, brand: string, cell: MatrixCellView): string {
		return this.translate.instant('ANALYTICS_COVERAGE_DASHBOARD.MATRIX_CELL_TITLE', {
			media,
			brand,
			count: formatAnalyticsInt(cell.count, this.lang),
			impact: formatSigned(cell.impact, 1, this.lang),
		});
	}

	// --- Coverage calendar ---

	get calendarLeadingBlanks(): number[] {
		const first = this.data?.calendar?.[0];
		if (!first?.date) return [];
		const weekday = new Date(first.date).getDay(); // 0 = Sunday
		const mondayIndexed = (weekday + 6) % 7; // 0 = Monday
		return Array.from({ length: mondayIndexed });
	}

	get calendarDays(): CalendarDayView[] {
		return (this.data?.calendar ?? []).map((d) => ({ day: d.date ? new Date(d.date).getDate() : 0, date: d.date || '', val: d.mentions ?? 0 }));
	}

	getCalendarHeatmap(val: number): string {
		if (val === 0) return 'bg-page text-ink-subtle';
		if (val === 1) return `bg-seq-2 ${seqTextColor(1)} font-medium`;
		if (val === 2) return `bg-seq-4 ${seqTextColor(3)} font-medium`;
		return `bg-seq-5 ${seqTextColor(4)} font-medium`;
	}

	calendarDayTitle(cd: CalendarDayView): string {
		return this.translate.instant('ANALYTICS_COVERAGE_DASHBOARD.CALENDAR_DAY_TITLE', { date: cd.date, count: formatAnalyticsInt(cd.val, this.lang) });
	}
}
